/**
 * useTranslate — calls the `translate-message` edge function.
 *
 * The edge function reads/writes the public.message_translations cache,
 * so repeat translations are instant and cost nothing.
 *
 * Public API:
 *   const { translate, translating } = useTranslate();
 *   const out = await translate("Hola", "en");
 */
import { useCallback, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface TranslateResponse {
  translated_text: string;
  cached: boolean;
}

export function useTranslate() {
  const [translating, setTranslating] = useState(false);

  const translate = useCallback(
    async (
      text: string,
      target_lang: string,
      source_lang?: string,
    ): Promise<{ text: string | null; cached: boolean; error: string | null }> => {
      if (!text?.trim()) return { text: null, cached: false, error: "empty" };
      setTranslating(true);
      try {
        const { data, error } = await supabase.functions.invoke<TranslateResponse>(
          "translate-message",
          { body: { text, target_lang, source_lang: source_lang ?? null } },
        );
        if (error || !data?.translated_text) {
          return { text: null, cached: false, error: error?.message ?? "translate_failed" };
        }
        return { text: data.translated_text, cached: data.cached, error: null };
      } finally {
        setTranslating(false);
      }
    },
    [],
  );

  return { translate, translating };
}
